import { Button } from '@/components/ui';
import React, { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import animation from '@/assets/branding/404.gif';
import { getDefaultMarket } from '@/app/router';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // You can also log the error to an error reporting service
    this.logErrorToMyService(error, errorInfo);
  }

  logErrorToMyService(error: Error, errorInfo: React.ErrorInfo) {
    // Example function to log errors
    console.error('Logged Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className="relative flex h-full items-center justify-center">
          <div className="relative z-10 flex flex-col items-center pb-16">
            <h1 className="text-[128px] font-bold leading-tight">Oops!</h1>
            <p className="max-w-3xl text-center text-lg text-foreground/75">
              You've encountered an unexpected error. Please contact support in
              our Discord and attach a screenshot of your browser console.
            </p>
            <div className="mt-12 flex gap-3">
              <HomePageLink />
              <Button
                size="lg"
                variant="secondary"
                className="h-14 rounded-none font-mono text-lg uppercase tracking-wider"
                asChild
              >
                <Link
                  to="https://discord.com/invite/vestexchange"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3"
                >
                  <svg
                    height="20"
                    viewBox="0 0 21 23"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                  >
                    <mask
                      id="mask0_5259_9306"
                      style={{ maskType: 'alpha' }}
                      maskUnits="userSpaceOnUse"
                      x="-1"
                      y="-1"
                      width="24"
                      height="24"
                    >
                      <rect
                        x="-1"
                        y="-1"
                        width="24"
                        height="24"
                        fill="url(#pattern0_5259_9306)"
                      />
                    </mask>
                    <g mask="url(#mask0_5259_9306)">
                      <rect
                        x="0.199219"
                        y="0.200108"
                        width="20.4"
                        height="22.8"
                        fill="currentColor"
                      />
                    </g>
                    <defs>
                      <pattern
                        id="pattern0_5259_9306"
                        patternContentUnits="objectBoundingBox"
                        width="1"
                        height="1"
                      >
                        <use
                          xlinkHref="#image0_5259_9306"
                          transform="scale(0.01)"
                        />
                      </pattern>
                      <image
                        id="image0_5259_9306"
                        width="100"
                        height="100"
                        xlinkHref="data:image/png;base64,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"
                      />
                    </defs>
                  </svg>
                  <span className="font-mono text-lg font-medium uppercase tracking-wider">
                    Discord
                  </span>
                </Link>
              </Button>
            </div>
          </div>
          <img
            className="absolute bottom-0 top-0 h-full opacity-50"
            src={animation}
            alt="404 error"
          />
        </div>
      );
    }

    return this.props.children;
  }
}

const HomePageLink = () => {
  const navigate = useNavigate();
  return (
    <Button
      size="lg"
      className="h-14 rounded-none font-mono text-lg uppercase tracking-wider"
      onClick={() => {
        navigate(`/trade/${getDefaultMarket()}`);
        navigate(0);
      }}
    >
      Back to homepage
    </Button>
  );
};

export default ErrorBoundary;
